/* PAWSONALITY_DUAL_PATH_V2 */
(() => {
  // Scroll-reveal: fade-and-rise sections/cards as they enter the viewport.
  // Only activates with JS + motion allowed, so content is never hidden otherwise.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const groups = [
    '.paw-dual-hero__copy > *',
    '.paw-dual-hero__visual .paw-dual-hero-card',
    '.paw-magic__copy, .paw-magic__stage',
    '.paw-lane--viral .paw-lane__heading, .paw-lane--viral .paw-product-editorial',
    '.paw-lane--custom .paw-lane__heading, .paw-lane--custom .paw-product-editorial',
    '.paw-style-theatre__heading, .paw-style-theatre__grid > a',
    '.paw-path-steps__heading, .paw-path-steps__grid > article',
    '.paw-moments__heading, .paw-moments__grid .paw-moment, .paw-moments__cta',
    '.paw-customer-stories__head, .paw-customer-story',
    '.paw-home-review-invite',
  ];
  const targets = [];
  groups.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el, index) => {
      el.classList.add('paw-reveal');
      el.style.setProperty('--paw-reveal-delay', `${Math.min(index, 7) * 80}ms`);
      targets.push(el);
    });
  });
  if (!targets.length) return;
  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-inview');
      revealObserver.unobserve(entry.target);
    });
  }, { threshold: .12, rootMargin: '0px 0px -8% 0px' });
  targets.forEach((el) => revealObserver.observe(el));
})();

(() => {
  // Moments videos: play only while on screen; posters stay under reduced motion or without JS.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const videos = document.querySelectorAll('[data-paw-moment-video]');
  if (!videos.length) return;
  const playObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const video = entry.target;
      if (entry.isIntersecting) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, { threshold: .25 });
  videos.forEach((video) => playObserver.observe(video));
})();

(() => {
  const root = document.querySelector('[data-paw-transformation]');
  if (!root || root.dataset.pawReady) return;
  root.dataset.pawReady = 'true';

  const button = root.querySelector('[data-paw-transform-toggle]');
  const status = root.querySelector('[data-paw-transform-status]');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let state = 'idle';
  let statusTimers = [];
  let completionTimer = 0;

  const track = (event, metadata = {}) => {
    window.PawConversion?.track(event, {
      lane: 'custom',
      location: 'homepage_transformation',
      creativeId: 'buddy_royal_photo_art_product_v1',
      ...metadata,
    });
  };

  const clearTimers = () => {
    statusTimers.forEach(window.clearTimeout);
    statusTimers = [];
    window.clearTimeout(completionTimer);
  };

  const setStatus = (text) => {
    if (status) status.textContent = text;
  };

  const play = (source = 'auto') => {
    if (reduced) return;
    clearTimers();
    root.classList.remove('is-playing', 'is-complete', 'is-paused');
    void root.offsetWidth;
    root.classList.add('is-playing');
    state = 'playing';
    button?.setAttribute('aria-pressed', 'false');
    if (button) button.textContent = 'Pause transformation';
    setStatus("Buddy's original photo");
    statusTimers.push(window.setTimeout(() => setStatus('Buddy · Royal portrait'), 1450));
    statusTimers.push(window.setTimeout(() => setStatus('Buddy · Framed wall art'), 3300));
    completionTimer = window.setTimeout(() => {
      root.classList.remove('is-playing', 'is-paused');
      root.classList.add('is-complete');
      state = 'complete';
      if (button) button.textContent = 'Replay transformation';
      track('transformation_completed', { source });
    }, 4650);
    track('transformation_played', { source });
  };

  const pause = () => {
    if (state !== 'playing') return;
    root.classList.add('is-paused');
    state = 'paused';
    button?.setAttribute('aria-pressed', 'true');
    if (button) button.textContent = 'Resume transformation';
    clearTimers();
  };

  const resume = () => {
    if (state !== 'paused') return;
    // Restart rather than guessing animation elapsed time; this keeps all
    // visual and spoken stage labels synchronized.
    play('resume');
  };

  button?.addEventListener('click', () => {
    if (state === 'playing') pause();
    else if (state === 'paused') resume();
    else play('replay');
  });

  if (reduced) {
    setStatus('Photo, portrait and finished product');
    root.classList.add('is-reduced-motion');
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    observer.disconnect();
    track('transformation_viewed');
    play('viewport');
  }, { threshold: .55 });
  observer.observe(root);
})();

(() => {
  // Hero style crossfade: ambient rotation through existing style frames in the
  // custom hero card. The base royal frame stays opaque underneath so the card
  // never goes translucent mid-fade; overlay frames fade in above it, and an
  // outgoing overlay is cleared silently once the incoming one covers it.
  // Runs only while the card is on screen and the tab is visible;
  // reduced-motion users keep the static base frame.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const card = document.querySelector('[data-paw-style-rotator]');
  if (!card) return;
  const layers = Array.from(card.querySelectorAll('.paw-hero-frame--layer'));
  if (!layers.length) return;

  // null = base frame showing (no overlay active).
  const states = [null, ...layers];
  let index = 0;
  let timer = 0;
  let cleanupTimer = 0;
  let cardInView = false;

  const clearSilently = (layer) => {
    layer.style.transition = 'none';
    layer.classList.remove('is-active');
    void layer.offsetWidth;
    layer.style.transition = '';
  };

  const advance = () => {
    const prev = states[index];
    index = (index + 1) % states.length;
    const next = states[index];
    window.clearTimeout(cleanupTimer);
    if (next) next.classList.add('is-active');
    if (!prev) return;
    if (next) {
      // Later-DOM overlays paint above earlier ones, so the incoming frame
      // covers the outgoing one; drop the covered frame after the fade.
      cleanupTimer = window.setTimeout(() => clearSilently(prev), 1200);
    } else {
      prev.classList.remove('is-active');
    }
  };
  const start = () => {
    if (!timer) timer = window.setInterval(advance, 3500);
  };
  const stop = () => {
    window.clearInterval(timer);
    timer = 0;
  };

  const viewObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      cardInView = entry.isIntersecting;
      if (cardInView && !document.hidden) start();
      else stop();
    });
  }, { threshold: .2 });
  viewObserver.observe(card);

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stop();
    else if (cardInView) start();
  });
})();
